import moment from 'moment';

import { DATAFEED_SYMBOL_TYPE, HEADER_SETTINGS_DEFAULTS, REGEX } from 'src/common/constants';
import { Rates, CurrencyValue } from 'src/types';
import { SupportLanguage } from 'src/types/configV2';

export function isEmptyObj(obj: object) {
    return Object.keys(obj).length == 0;
}

export function isObj(obj: object) {
    return obj !== undefined && obj !== null && Object.keys(obj).length;
}

export function isJsonString(str: string) {
    try {
        JSON.parse(str);
    } catch (e) {
        return false;
    }
    return true;
}

export function currencyFormatter(value: number, minDecimal: number, maxDecimal?: number) {
    let formatter = new Intl.NumberFormat('en-US', {
        minimumFractionDigits: minDecimal < 0 ? 0 : minDecimal,
    });
    if (maxDecimal !== undefined) {
        formatter = new Intl.NumberFormat('en-US', {
            minimumFractionDigits: minDecimal < 0 ? 0 : minDecimal,
            maximumFractionDigits: maxDecimal < 0 ? 0 : maxDecimal,
        });
    }
    return formatter.format(value);
}

export function msToTime(milliSeconds: number) {
    if (!milliSeconds) {
        return ['00', '00', '00', '00'];
    }
    const hourMilliseconds = 60 * 60 * 1000;
    const dayMilliseconds = 24 * hourMilliseconds;
    const days = Math.floor(milliSeconds / dayMilliseconds);
    const hourDivider = milliSeconds - days * dayMilliseconds;
    const hours = Math.floor(hourDivider / hourMilliseconds);
    const minuteDivider = hourDivider - hours * hourMilliseconds;
    const minutes = Math.floor(minuteDivider / (60 * 1000));
    const seconds = Math.floor((minuteDivider - minutes * 60 * 1000) / 1000);
    return [
        days.toString().padStart(2, '0'),
        hours.toString().padStart(2, '0'),
        minutes.toString().padStart(2, '0'),
        seconds.toString().padStart(2, '0'),
    ];
}

/**
 * Rounds or truncates a number based on the specified precision.
 * If precision is positive, the number is rounded to the specified number of decimal places.
 * If precision is zero, the number is truncated to an integer.
 * If precision is negative, the number is truncated to that many digits before the decimal point.
 *
 * 12.10 -> 12.1 (for precision = 1)
 * 12.11 -> 12.1 (for precision = 1, rounded)
 * 12.11 -> 12 (for precision = 0, truncated)
 * 123.45 -> 120 (for precision = -2, truncated)
 *
 * @param {Number} value - The number to round or truncate.
 * @param {Number} precision - The number of decimal places or digits before the decimal point.
 * @returns {Number} - The rounded or truncated number.
 */
export function digits(value: number, precision = 0): number {
    if (precision > 0) {
        const multiplier = Math.pow(10, precision);
        const roundedValue = Math.round(value * multiplier) / multiplier;
        return Number(roundedValue.toFixed(precision));
    } else if (precision === 0) {
        return Math.floor(value);
    } else {
        const multiplier = Math.pow(10, -precision);
        return Math.floor(value / multiplier) * multiplier;
    }
}

/**
 * Rounds a number upward (ceil) based on the given precision, with special handling for large or small numbers.
 * Uses `mathjs` for high precision during multiplication and division, ensuring accuracy for large decimal values.
 * If precision is zero or invalid, the number is rounded up to the nearest integer.
 * If precision is negative, the number is rounded up based on digits before the decimal point.
 * If precision is positive, the number is rounded up to the specified decimal places.
 *
 * 12.10 -> 12.1 (for precision = 1)
 * 12.11 -> 12.2 (for precision = 1)
 * 12.11 -> 13 (for precision = 0, rounded up)
 * 123.45 -> 130 (for precision = -1)
 *
 * @param {Number} v - The number to round.
 * @param {Number} d - The number of decimal places or digits before the decimal point.
 * @returns {Number} - The rounded number.
 */
export const digits2 = (value: number, precision = 0): number => {
    if (precision <= 0) {
        return Math.ceil(value);
    }
    const pow = Math.pow(10, precision);
    const roundedValue = Math.ceil(value * pow) / pow;
    return digits(roundedValue, precision);
};

/**
 * Truncates a number to the specified precision.
 * If precision is positive, the number is truncated to that many decimal places.
 * If precision is zero, the number is truncated to an integer.
 * If precision is negative, the number is truncated to that many digits before the decimal point.
 *
 * 12.10 -> 12.1 (for precision = 1)
 * 12.11 -> 12.1 (for precision = 1)
 * 12.11 -> 12 (for precision = 0)
 * 123.45 -> 120 (for precision = -2)
 *
 * @param {Number} value - The number to truncate.
 * @param {Number} precision - The number of decimal places or digits before the decimal point.
 * @returns {Number} - The truncated number.
 */
export function digits3(value: number, precision = 0): number {
    if (precision > 0) {
        const multiplier = Math.pow(10, precision);
        return Math.floor(value * multiplier) / multiplier;
    } else if (precision === 0) {
        return Math.floor(value);
    } else {
        const multiplier = Math.pow(10, -precision);
        return Math.floor(value / multiplier) * multiplier;
    }
}

export function roundNumber(value: number, precision: number): number {
    if (countDecimals(value) <= precision) {
        return value;
    }
    return Math.trunc(value * Math.pow(10, precision)) / Math.pow(10, precision);
}

function countDecimals(value: number) {
    if (Math.floor(value.valueOf()) === value.valueOf()) return 0;
    const decimalPart = value.toString().split('.')[1];
    return decimalPart ? decimalPart.length : 0;
}

export function changeScrollBehavior(isDisableScroll: boolean) {
    // if (isDisableScroll) {
    //     window.onscroll = () => window.scroll(0, 0);
    // } else {
    //     window.onscroll = () => '';
    // }
    if (isDisableScroll) {
        document.body.style.overflowY = 'hidden';
    } else {
        document.body.style.overflowY = 'auto';
    }
}

export function currencyValue(
    currencyRate: Rates,
    value: number,
    supportedLanguages: SupportLanguage[],
    suffix = false,
    choose = 'en-us',
): CurrencyValue {
    const supportedCurrencies = supportedLanguages.map((currency) => ({
        code: currency.suffix,
        symbol: currency.prefix,
        name: currency.lang,
    }));

    const currency = supportedCurrencies.find((currency) => currency.name === choose);

    if (!currency) {
        return suffix ? { name: '', value: '--', endName: '' } : { name: '', value: '--' };
    }

    const name = currency.name;
    const endName = currency.code;
    let v = parseFloat(currencyRate[endName as keyof Rates]) * value;

    if (isNaN(v) || v < 0) {
        return suffix ? { name, value: '--', endName } : { name, value: '--' };
    }

    const fix = v - 0.1 < 0 && v !== 0 ? 5 : 2;
    v = digits(v, fix);
    return suffix
        ? { name, value: currencyFormatter(v, fix), endName }
        : { name, value: currencyFormatter(v, fix) };
}

export function isValidRegex(regexString: string): boolean {
    try {
        new RegExp(regexString);
        return true;
    } catch (e) {
        return false;
    }
}

export function hasCommonValue(arr1: string[], arr2: string[]): boolean {
    const set = new Set(arr1);
    for (let i = 0; i < arr2.length; i++) {
        if (set.has(arr2[i])) {
            return true;
        }
    }
    return false;
}

export function DateTimeFormatter(value: number, format: string = 'YYYY-MM-DD HH:mm:ss') {
    return moment.utc(value).local().format(format);
}

export function noNan(value: number) {
    return isNaN(value) ? 0 : value;
}

export function getDecimalPartCount(num: Number) {
    if (num == 0) {
        return 1;
    }
    const decimalCount = num.toString().split('.');
    if (decimalCount.length > 1) {
        return Number(decimalCount[1].length);
    } else {
        return 1;
    }
}

export const getResolution = (interval: string): string => {
    const resolutionMap: { [key: string]: string } = {
        '60': '1h',
        '120': '2h',
        '240': '4h',
        '360': '6h',
        '720': '12h',
        '1440': '1d',
        '1D': '1d',
        '1W': '1w',
        '1M': '1M',
    };

    if (resolutionMap[interval]) {
        interval = resolutionMap[interval];
    } else if (/^\d{1,}$/g.test(interval)) {
        interval += 'm';
    }

    return interval || '1m';
};

export const getHoursFromResolution = (interval: string): number => {
    let resolution: number;
    const resolutionMap: { [key: string]: number } = {
        '60': 60,
        '120': 120,
        '240': 240,
        '360': 360,
        '720': 720,
        '1440': 1440,
        '1D': 1440,
        '1W': 10080,
        '1M': 44640,
    };

    if (resolutionMap[interval]) {
        resolution = resolutionMap[interval];
    } else {
        resolution = Number(interval);
    }

    return resolution;
};

export const removeCommas = (value: string) => {
    return value.replace(/,/g, '');
};

export const contractNameFormatter = (value: string) => {
    return value.replace(REGEX.PERPETUAL_CONTRACT_POSTFIX, '');
};

export const stringReform = (str: string): string => {
    let result: string = '';
    let c: string;
    for (let i: number = 0; i < str.length; i++) {
        c = str.substring(i, i + 1);
        if (c == '\n') {
            result = `${result}</br>`;
        } else if (c == ' ' || c == '\\s') {
            result = `${result} `;
        } else if (c != '\r') {
            result = result + c;
        }
    }
    return result;
};

export const extractSymbolName = (symbolName: string, type: string) => {
    if (type === DATAFEED_SYMBOL_TYPE.PERPETUAL) {
        const underscoreIndex = symbolName.indexOf('_');
        if (underscoreIndex !== -1) {
            return symbolName.substring(underscoreIndex + 1).slice(0, -2);
        } else if (symbolName.endsWith('-P')) {
            return symbolName.slice(0, -2);
        }
    } else if (type === DATAFEED_SYMBOL_TYPE.SWAP) {
        const dashIndex = symbolName.indexOf('-');
        if (dashIndex !== -1) {
            const parts = symbolName.split('-').filter((part) => part !== 'SWAP');
            return parts.join('');
        }
    }
    return symbolName;
};

export const parseTradeSettings = (chartSettings: typeof HEADER_SETTINGS_DEFAULTS) => {
    if (!chartSettings) {
        return HEADER_SETTINGS_DEFAULTS;
    } else {
        return {
            favoriteChartTypes:
                chartSettings?.favoriteChartTypes || HEADER_SETTINGS_DEFAULTS.favoriteChartTypes,
            activeChartType:
                chartSettings?.activeChartType || HEADER_SETTINGS_DEFAULTS.activeChartType,
            favoriteIntervals:
                chartSettings?.favoriteIntervals || HEADER_SETTINGS_DEFAULTS.favoriteIntervals,
            chartInterval: chartSettings?.chartInterval || HEADER_SETTINGS_DEFAULTS.chartInterval,
        };
    }
};

export const copyToClipboard = async (value: string) => {
    await navigator.clipboard.writeText(value);
};

export const abbreviateNumber = (value: string): string => {
    const numberValue = parseFloat(value);
    if (isNaN(numberValue)) {
        return value; // Return the original string if parsing fails
    }

    const abbreviations = ['', 'K', 'M', 'B', 'T'];
    const numDigits = Math.floor(Math.log10(Math.abs(numberValue))) + 1;

    if (numDigits <= 3) {
        return value; // Return the original string if the number is not large enough
    }

    const index = Math.floor((numDigits - 1) / 3);
    const roundedValue = (numberValue / Math.pow(10, index * 3)).toFixed(2); // Round to 2 decimal places

    return `${roundedValue}${abbreviations[index]}`;
};

const isValidHex = (hex: string) => /^#([A-Fa-f0-9]{3,4}){1,2}$/.test(hex);

const getChunksFromString = (st: string, chunkSize: number) =>
    st.match(new RegExp(`.{${chunkSize}}`, 'g'));

const convertHexUnitTo256 = (hexStr: string) => parseInt(hexStr.repeat(2 / hexStr.length), 16);

const getAlphafloat = (a: number, alpha: number) => {
    if (typeof a !== 'undefined') {
        return a / 255;
    }

    if (typeof alpha !== 'number' || alpha < 0 || alpha > 1) {
        return 1;
    }
    return alpha;
};

export const hexToRGBA = (hex: string, alpha: number) => {
    if (!isValidHex(hex)) {
        throw new Error('Invalid HEX');
    }
    const chunkSize = Math.floor((hex.length - 1) / 3);
    const hexArr = getChunksFromString(hex.slice(1), chunkSize);
    const [r, g, b, a] = (hexArr ?? []).map(convertHexUnitTo256);
    return `rgba(${r}, ${g}, ${b}, ${getAlphafloat(a, alpha)})`;
};
